import { useId } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "components/common/buttons/Button";
import { Checkbox } from "components/common/checkbox";
import type { EffectiveRateCardProps } from "./EffectiveRateCard.types";
import type { TaxIncentiveAssumptions } from "../taxIncentiveEffectiveRate.types";
import "./TaxIncentiveDialog.css";

type TaxIncentiveDialogProps = Omit<EffectiveRateCardProps, "compact"> & {
  triggerLabel?: string;
  assumptions?: TaxIncentiveAssumptions;
  onAssumptionsChange?: (assumptions: TaxIncentiveAssumptions) => void;
};

export function TaxIncentiveDialog({
  summary,
  currencySymbol,
  assumptions,
  onAssumptionsChange,
  triggerLabel,
}: TaxIncentiveDialogProps) {
  const crewInputId = useId();
  const money = (value: number) => `${currencySymbol}${Math.round(value).toLocaleString("en-US")}`;
  const belowMinimum = !summary.meetsMinimumSpend && summary.minimumSpend !== undefined;
  let remainingCredit = summary.headlineCredit;
  const deductions = summary.frictionBreakdown.map((line) => {
    remainingCredit -= line.amount;
    return { ...line, remainingCredit: Math.max(0, remainingCredit) };
  });
  const scale = Math.max(summary.headlineCredit, summary.totalFriction, 1);
  const chartColumns = { gridTemplateColumns: `repeat(${deductions.length + 2}, minmax(0, 1fr))` };

  const updateCrew = (value: string) => {
    if (!assumptions || !onAssumptionsChange || value.trim() === "") return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    onAssumptionsChange({
      ...assumptions,
      residentCrewPercent: Math.max(0, Math.min(100, Math.round(parsed))),
    });
  };

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="tax-incentive-trigger"
          aria-label={triggerLabel ?? "Tax incentive details"}
        >
          {triggerLabel ?? "Tax incentives"}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="tax-incentive-backdrop" />
        <Dialog.Content className="tax-incentive-dialog">
          <header className="tax-incentive-dialog-head">
            <div>
              <Dialog.Title>{summary.primaryJurisdiction || "Tax"} tax credit</Dialog.Title>
              <Dialog.Description>
                Estimated incentive · {summary.headlineRatePercent.toFixed(1)}% headline rate
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button
                type="button"
                color="gray"
                size="icon"
                className="h-[34px] w-[34px] shrink-0 text-[22px] font-normal"
                aria-label="Close estimate"
              >
                ×
              </Button>
            </Dialog.Close>
          </header>
          <div className="tax-incentive-outcomes" aria-live="polite">
            <div>
              <span>Net credit</span>
              <strong className="tax-incentive-credit">{money(summary.effectiveNetCredit)}</strong>
            </div>
            <div>
              <span>Net budget</span>
              <strong>{money(summary.netCost)}</strong>
            </div>
            <div className="tax-incentive-rate">
              <span>Effective rate</span>
              <strong>{summary.effectiveRatePercent.toFixed(1)}%</strong>
              <small>of qualified spend</small>
            </div>
          </div>
          {belowMinimum ? (
            <div className="tax-incentive-minimum">
              <strong>No credit at this spend level</strong>
              <p>
                Qualified spend is {money(summary.qualifiedSpend)}. This program requires at least{" "}
                {money(summary.minimumSpend ?? 0)}.
              </p>
            </div>
          ) : (
            <section className="tax-incentive-chart" aria-label="Credit breakdown">
              <div className="tax-incentive-chart-heading">
                <h3>From headline to net credit</h3>
                <span>Headline credit less costs</span>
              </div>
              <div
                className="tax-incentive-bars"
                style={chartColumns}
                role="img"
                aria-label={`${money(summary.headlineCredit)} headline credit, less ${money(summary.totalFriction)} in costs, gives ${money(summary.effectiveNetCredit)} net credit.`}
              >
                <div>
                  <i
                    className="tax-incentive-bar headline"
                    style={{ height: `${(summary.headlineCredit / scale) * 100}%`, bottom: 0 }}
                  />
                </div>
                {deductions.map((line) => (
                  <div key={line.label}>
                    <i
                      className="tax-incentive-bar deduction"
                      style={{
                        bottom: `${(line.remainingCredit / scale) * 100}%`,
                        height: `${(line.amount / scale) * 100}%`,
                      }}
                    />
                  </div>
                ))}
                <div>
                  <i
                    className="tax-incentive-bar net"
                    style={{ height: `${(summary.effectiveNetCredit / scale) * 100}%`, bottom: 0 }}
                  />
                </div>
              </div>
              <div className="tax-incentive-chart-labels" style={chartColumns}>
                <div>
                  <strong>{money(summary.headlineCredit)}</strong>
                  <span>Headline credit</span>
                </div>
                {deductions.map((line) => (
                  <div key={line.label}>
                    <strong className="tax-incentive-loss">−{money(line.amount)}</strong>
                    <span>{line.label}</span>
                  </div>
                ))}
                <div>
                  <strong className="tax-incentive-credit">
                    {money(summary.effectiveNetCredit)}
                  </strong>
                  <span>Net credit</span>
                </div>
              </div>
            </section>
          )}
          {assumptions && onAssumptionsChange ? (
            <section className="tax-incentive-assumptions">
              <h3>Production assumptions</h3>
              <div className="tax-incentive-assumption-fields">
                <div>
                  <div className="tax-incentive-crew-label">
                    <label htmlFor={crewInputId}>Resident crew</label>
                    <div>
                      <input
                        id={crewInputId}
                        type="number"
                        min={0}
                        max={100}
                        value={assumptions.residentCrewPercent}
                        onChange={(event) => updateCrew(event.target.value)}
                      />
                      <span>%</span>
                    </div>
                  </div>
                  <input
                    aria-label="Resident crew percentage"
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={assumptions.residentCrewPercent}
                    onChange={(event) => updateCrew(event.target.value)}
                  />
                  <div className="tax-incentive-range-labels">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
                <div className="tax-incentive-checks">
                  <label>
                    <Checkbox
                      name="tax-promotional-uplift"
                      checked={assumptions.applyPromotionalUplift}
                      onChange={() =>
                        onAssumptionsChange({
                          ...assumptions,
                          applyPromotionalUplift: !assumptions.applyPromotionalUplift,
                        })
                      }
                    />
                    <span
                      className={
                        assumptions.applyPromotionalUplift
                          ? "tax-incentive-option-checked"
                          : undefined
                      }
                    >
                      Promotional uplift
                    </span>
                  </label>
                  <label>
                    <Checkbox
                      name="tax-location-uplift"
                      checked={assumptions.applyLocationUplift}
                      onChange={() =>
                        onAssumptionsChange({
                          ...assumptions,
                          applyLocationUplift: !assumptions.applyLocationUplift,
                        })
                      }
                    />
                    <span
                      className={
                        assumptions.applyLocationUplift ? "tax-incentive-option-checked" : undefined
                      }
                    >
                      Location uplift
                    </span>
                  </label>
                  <label>
                    <Checkbox
                      name="tax-financing"
                      checked={assumptions.usesFinancing}
                      onChange={() =>
                        onAssumptionsChange({
                          ...assumptions,
                          usesFinancing: !assumptions.usesFinancing,
                        })
                      }
                    />
                    <span
                      className={
                        assumptions.usesFinancing ? "tax-incentive-option-checked" : undefined
                      }
                    >
                      Financing bridge loan
                    </span>
                  </label>
                </div>
              </div>
            </section>
          ) : null}
          <p className="tax-incentive-basis">
            Qualified spend {money(summary.qualifiedSpend)}. Gross budget{" "}
            {money(summary.grossBudget)}.
          </p>
          <footer className="tax-incentive-dialog-foot">
            <p>Estimates only. Not tax or legal advice.</p>
            <Dialog.Close asChild>
              <Button type="button" size="sm">
                Done
              </Button>
            </Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
